/**
 * The green chip.
 *
 * This component is the bootstrap's definition of done: it must render green
 * at localhost, at dev.<domain>, and at <domain>. It proves, in one glance,
 * that the frontend was built, the API is reachable across whatever CORS and
 * DNS sit between them, the generated types match the live response, and the
 * backend can reach DynamoDB and S3.
 *
 * It also displays which environment the API believes it is, which catches the
 * classic mistake of pointing the dev frontend at the prod API.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { api, APP_ENV } from '../api/client';
import type { components } from '../api/schema';

// Straight off the generated schema, so it is exactly the Pydantic model.
// Change Health in backend/app/models.py, run `make types`, and every use of
// this type updates -- or fails to compile, which is the point.
type Health = components['schemas']['Health'];

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; health: Health }
  | { kind: 'error'; message: string };

/**
 * Pure probe: performs the request and returns the next state. Keeping it out of
 * the component means the effect below never calls setState synchronously, which
 * is both what react-hooks/set-state-in-effect wants and a cleaner split between
 * I/O and state.
 */
async function probe(): Promise<State> {
  try {
    const { data, error, response } = await api.GET('/api/health');
    // Read status up front: once `error` is narrowed, TS collapses `response`
    // to never, because the schema declares no error responses for this route.
    const status = response.status;

    // A degraded backend answers 503, which openapi-fetch surfaces as `error`
    // rather than `data` -- but the body is still the Health payload, and it is
    // the one that says *which* dependency is down. Prefer it over a bare code.
    const body = (data ?? error) as Health | undefined;
    if (!body || typeof body.status !== 'string') {
      return { kind: 'error', message: `API returned HTTP ${status}` };
    }
    return { kind: 'ok', health: body };
  } catch (e) {
    // Network-level failure: wrong host, DNS not propagated, or CORS refused.
    return {
      kind: 'error',
      message: e instanceof Error ? e.message : 'Could not reach the API',
    };
  }
}

export function ApiStatus() {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const next = await probe();
      if (!cancelled) setState(next);
    };

    void run();
    const t = setInterval(() => void run(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Note: MUI v9 removed the layout system props from Stack and Typography.
  // alignItems, flexWrap, useFlexGap and display all go through sx now.
  if (state.kind === 'loading') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Contacting API…
        </Typography>
      </Stack>
    );
  }

  if (state.kind === 'error') {
    return (
      <Alert severity="error" variant="outlined">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          API unreachable
        </Typography>
        <Typography variant="caption">{state.message}</Typography>
      </Alert>
    );
  }

  const { health } = state;
  const healthy = health.status === 'ok';
  // A frontend built for one environment talking to another's API is a real
  // misconfiguration, even when both are individually healthy.
  const envMismatch = health.env !== APP_ENV;

  return (
    <Card variant="outlined" sx={{ maxWidth: 480 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Chip
              icon={healthy ? <CheckCircleIcon /> : <ErrorIcon />}
              label={healthy ? 'API connected' : 'API degraded'}
              color={healthy ? 'success' : 'error'}
              size="small"
            />
            <Chip label={`env: ${health.env}`} size="small" variant="outlined" />
            <Chip label={`v${health.version}`} size="small" variant="outlined" />
          </Stack>

          {envMismatch && (
            <Alert severity="warning" variant="outlined">
              This frontend is built for <strong>{APP_ENV}</strong> but the API reports{' '}
              <strong>{health.env}</strong>. Check VITE_API_BASE_URL.
            </Alert>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
              Dependencies
            </Typography>
            <Stack direction="row" spacing={1}>
              {/* Cast because the generated schema types deps as an object with
                  known keys; Object.entries widens the values to unknown. */}
              {(Object.entries(health.deps) as [string, string][]).map(([name, status]) => (
                <Tooltip key={name} title={status}>
                  <Chip
                    label={name}
                    size="small"
                    color={status === 'ok' ? 'success' : 'error'}
                    variant={status === 'ok' ? 'filled' : 'outlined'}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>

          {/* Non-null means the Loki handler is failing silently -- the one thing
              a fire-and-forget logging path can't tell you any other way. */}
          {health.logs_dropped != null && health.logs_dropped > 0 && (
            <Alert severity="warning" variant="outlined">
              {health.logs_dropped} log lines failed to reach Grafana.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
